import React from "react";
import { motion } from "motion/react";
import "../../styles/dimension-allocation.css";

// Generic 0–100 multi-dimension allocation editor, used for both the PAEI and
// CAPI job tabs. Each dimension gets a synced slider + number input, plus a
// bar chart (dimensions on the x-axis, 0–100 on the y-axis) that animates as
// values change.
const clamp = (n) => Math.max(0, Math.min(100, Number.isFinite(n) ? n : 0));

const CHART_GRIDLINES = [0, 25, 50, 75, 100];

const DimensionAllocationPanel = ({
  title,
  subtitle,
  dimensions, // [{ key, label, description, color, bg }]
  values, // { [key]: number }
  onChange, // (key, number) => void
  disabled,
}) => {
  return (
    <div className="role-card dap-panel">
      <div className="dap-header">
        <div>
          <div className="dap-title">{title}</div>
          {subtitle && <div className="dap-subtitle">{subtitle}</div>}
        </div>
      </div>

      <div className="dap-chart" role="img" aria-label={`${title} bar chart`}>
        <div className="dap-chart-plot">
          {CHART_GRIDLINES.slice()
            .reverse()
            .map((g) => (
              <div className="dap-chart-gridline" key={g} style={{ bottom: `${g}%` }}>
                <span className="dap-chart-gridline-label">{g}</span>
              </div>
            ))}
          {dimensions.map((d) => {
            const value = clamp(Number(values?.[d.key]));
            const accent = d.accent || d.color;
            return (
              <div className="dap-chart-col" key={d.key}>
                <div className="dap-chart-bar-track">
                  <motion.div
                    className="dap-chart-value"
                    initial={{ bottom: 0 }}
                    animate={{ bottom: `${value}%` }}
                    transition={{ type: "spring", stiffness: 260, damping: 28 }}
                  >
                    {value}
                  </motion.div>
                  <motion.div
                    className="dap-chart-bar"
                    style={{ background: accent }}
                    initial={{ height: 0 }}
                    animate={{ height: `${value}%` }}
                    transition={{ type: "spring", stiffness: 260, damping: 28 }}
                  />
                </div>
                <div className="dap-chart-col-label">{d.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="dap-rows">
        {dimensions.map((d) => {
          const value = clamp(Number(values?.[d.key]));
          const accent = d.accent || d.color;
          return (
            <div className="dap-row" key={d.key}>
              <div className="dap-row-label">
                <span
                  className="dap-badge"
                  style={{ background: d.bg, color: d.color }}
                >
                  {d.key.charAt(0).toUpperCase()}
                </span>
                <div>
                  <div className="dap-row-name">{d.label}</div>
                  {d.description && (
                    <div className="dap-row-desc">{d.description}</div>
                  )}
                </div>
              </div>
              <div className="dap-row-controls">
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={value}
                  disabled={disabled}
                  onChange={(e) => onChange(d.key, clamp(Number(e.target.value)))}
                  style={{ accentColor: accent }}
                  aria-label={`${d.label} value`}
                />
                <div className="dap-row-number">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={value}
                    disabled={disabled}
                    onChange={(e) =>
                      onChange(d.key, clamp(Number(e.target.value)))
                    }
                    aria-label={`${d.label} value`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DimensionAllocationPanel;
